(() => {
  if (window.__rcBundleCartEditInitialized) return;

  window.__rcBundleCartEditInitialized = true;

  const STORAGE_KEY = 'rechargeBundleCartEdit';
  const EDIT_SELECTOR = '[data-recharge-bundle-edit]';
  const EDIT_TOKEN_PARAM = 'bundle-edit';
  const MAX_EDIT_AGE = 30 * 60 * 1000;
  const POLL_ATTEMPTS = 20;
  const POLL_DELAY = 500;
  const routesRoot = window.Shopify?.routes?.root || '/';
  const cartJsonUrl = `${routesRoot}cart.js`;
  const cartUpdateUrl = `${routesRoot}cart/update.js`;
  let activePendingEdit = null;

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options.headers,
      },
    });

    if (!response.ok) { 
      throw new Error(`Cart request failed with status ${response.status}`);
    }

    return response.json();
  };

  const getCart = () => requestJson(cartJsonUrl, { cache: 'no-store' });

  const getBundleId = item => item.properties?._rc_bundle;

  const getBundleIds = cart => [
    ...new Set(cart.items.map(getBundleId).filter(Boolean)),
  ];

  const getBundleItems = (cart, bundleId) => cart.items.filter(
    item => getBundleId(item) === bundleId,
  );

  const getBundleContents = bundleItems => {
    const selections = bundleItems.reduce((bundleSelections, item) => {
      const productId = String(item.product_id || '');
      const variantId = String(item.variant_id || '');
      const quantity = Number(item.quantity);

      if (!productId || !variantId || !Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Recharge bundle contents contain an invalid cart line.');
      }

      const selectionId = `${productId}:${variantId}`;
      bundleSelections.set(
        selectionId,
        (bundleSelections.get(selectionId) || 0) + quantity,
      );
      return bundleSelections;
    }, new Map());

    if (selections.size === 0) {
      throw new Error('Recharge bundle contents were not found in the cart.');
    }

    return [...selections.entries()]
      .map(([selectionId, quantity]) => `${selectionId}:${quantity}`)
      .join(',');
  };

  const readPendingEdit = () => {
    const storedEdit = sessionStorage.getItem(STORAGE_KEY);
    if (!storedEdit) return null;

    try {
      const pendingEdit = JSON.parse(storedEdit);

      if (Date.now() - pendingEdit.startedAt > MAX_EDIT_AGE) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return pendingEdit;
    } catch (error) {
      console.error('Unable to read the pending Recharge bundle edit.', error);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  };

  const isPendingEditPage = pendingEdit => (
    pendingEdit
    && window.location.pathname === pendingEdit.bundlePath
    && new URLSearchParams(window.location.search).get(EDIT_TOKEN_PARAM)
      === pendingEdit.editToken
  );

  const setEditButtonTranslations = () => {
    const language = (document.documentElement.lang || 'en').split('-')[0];
    window.StorefrontBundlingWidget = window.StorefrontBundlingWidget || {};
    const customTranslations = window.StorefrontBundlingWidget.customTranslations || {};
    window.StorefrontBundlingWidget.customTranslations = {
      ...customTranslations,
      [language]: {
        ...(customTranslations[language] || {}),
        general_add_to_cart: 'Update cart',
        general_add_to_cart_price: 'Update cart: <price/>',
      },
    };
  };

  const wait = duration => new Promise(resolve => {
    window.setTimeout(resolve, duration);
  });

  const waitForReplacementBundle = async pendingEdit => {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
      const cart = await getCart();
      const replacementBundleId = getBundleIds(cart).find(
        bundleId => !pendingEdit.bundleIdsBefore.includes(bundleId),
      );

      if (replacementBundleId) return cart;
      await wait(POLL_DELAY);
    }

    throw new Error('Recharge did not add a replacement bundle in time.');
  };

  const removeOriginalBundle = async (cart, originalBundleId) => {
    const updates = cart.items.reduce((bundleUpdates, item) => {
      if (getBundleId(item) === originalBundleId) {
        bundleUpdates[item.key] = 0;
      }

      return bundleUpdates;
    }, {});

    if (Object.keys(updates).length === 0) return;

    await requestJson(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
  };

  const replaceEditedBundle = async pendingEdit => {
    const cartWithReplacement = await waitForReplacementBundle(pendingEdit);
    await removeOriginalBundle(cartWithReplacement, pendingEdit.originalBundleId);
    sessionStorage.removeItem(STORAGE_KEY);
    window.location.assign(pendingEdit.returnUrl);
  };

  const startBundleEdit = async link => {
    const cart = await getCart();
    const bundleUrl = new URL(link.href, window.location.href);
    const originalBundleId = link.dataset.rechargeBundleId;

    if (!originalBundleId) {
      throw new Error('Recharge bundle ID is missing from the edit link.');
    }

    const bundleItems = getBundleItems(cart, originalBundleId);
    const bundleVariantId = bundleItems[0]?.properties?._rc_bundle_variant;
    const editToken = window.crypto.randomUUID();
    bundleUrl.searchParams.set('contents', getBundleContents(bundleItems));
    bundleUrl.searchParams.set(EDIT_TOKEN_PARAM, editToken);

    if (bundleVariantId) {
      bundleUrl.searchParams.set('variant', String(bundleVariantId));
    } else {
      bundleUrl.searchParams.delete('variant');
    }

    const pendingEdit = {
      originalBundleId,
      bundleIdsBefore: getBundleIds(cart),
      bundlePath: bundleUrl.pathname,
      editToken,
      returnUrl: `${routesRoot}cart`,
      startedAt: Date.now(),
    };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pendingEdit));
    window.location.assign(bundleUrl.href);
  };

  const handleEditClick = async event => {
    const link = event.target.closest(EDIT_SELECTOR);
    if (!link) return;

    event.preventDefault();

    try {
      await startBundleEdit(link);
    } catch (error) {
      console.error('Unable to start the Recharge bundle edit.', error);
      window.location.assign(link.href);
    }
  };

  const existingAddToCart = window.RechargeBundle?.onAddToCart;

  const handleRechargeAddToCart = async (...args) => {
    if (typeof existingAddToCart === 'function') {
      await existingAddToCart(...args);
    }

    if (!isPendingEditPage(activePendingEdit)) return;

    try {
      await replaceEditedBundle(activePendingEdit);
    } catch (error) {
      console.error('Unable to replace the Recharge bundle in the cart.', error);
    }
  };

  document.addEventListener('click', handleEditClick);
  activePendingEdit = readPendingEdit();

  if (isPendingEditPage(activePendingEdit)) {
    setEditButtonTranslations();
    window.RechargeBundle = window.RechargeBundle || {};
    window.RechargeBundle.onAddToCart = handleRechargeAddToCart;
  }
})();
